import React, { Component } from 'react';
import PropTypes from 'prop-types';
import BodyClassName from 'react-body-classname';
import { graphql, gql, compose } from 'react-apollo';
import { injectIntl, intlShape } from 'react-intl';
import classnames from 'classnames';

import FloatingActionButton from 'material-ui/FloatingActionButton';
import IconButton from 'material-ui/IconButton';
import RaisedButton from 'material-ui/RaisedButton';
import AutoComplete from 'material-ui/AutoComplete';
import TextField from 'material-ui/TextField';
import Snackbar from 'material-ui/Snackbar';

import Pencil from 'material-ui/svg-icons/editor/mode-edit';
import Highlighter from 'material-ui/svg-icons/editor/format-paint';
import Text from 'material-ui/svg-icons/editor/title';
import SvgIcon from 'material-ui/SvgIcon';

import Close from 'material-ui/svg-icons/navigation/close';
import ExpandMore from 'material-ui/svg-icons/navigation/expand-more';
import ExpandLess from 'material-ui/svg-icons/navigation/expand-less';
import VisibilityOff from 'material-ui/svg-icons/action/speaker-notes-off';
import Rotate from 'material-ui/svg-icons/av/replay';
import { mdiClipboardCheckOutline } from '@mdi/js';

import styles from './Paper.css';

import Page from './Page';

import previousIcon from './previous.svg';
import nextIcon from './next.svg';

const Eraser = props => (
    <SvgIcon {...props}>
        <path d="M16.24,3.56L21.19,8.5C21.97,9.29 21.97,10.55 21.19,11.34L12,20.53C10.44,22.09 7.91,22.09 6.34,20.53L2.81,17C2.03,16.21 2.03,14.95 2.81,14.16L13.41,3.56C14.2,2.78 15.46,2.78 16.24,3.56M4.22,15.58L7.76,19.11C8.54,19.9 9.8,19.9 10.59,19.11L14.12,15.58L9.17,10.63L4.22,15.58Z" />
    </SvgIcon>
);
const Criteria = props => (
    <SvgIcon {...props}>
        <path d={mdiClipboardCheckOutline} />
    </SvgIcon>
);

const updateJuryMarksMutation = gql`
    mutation updateJuryMarksMutation(
        $paperId: ID!
        $newJuryMarks: [JuryMarkInput]
        $juryMarksToDelete: [JuryMarkInput]
    ) {
        updateJuryMarks(paperId: $paperId, newJuryMarks: $newJuryMarks, juryMarksToDelete: $juryMarksToDelete) {
            user {
                _id
                name
            }
            mark
            stage
            problemName
        }
    }
`;

const createPageCommentMutation = gql`
    mutation CreatePageCommentMutation($pageId: ID!, $text: String, $path: String, $style: String) {
        createPageComment(pageId: $pageId, text: $text, path: $path, style: $style) {
            _id
            user {
                _id
                name
            }
            path
            style
            text
        }
    }
`;

const deletePageCommentMutation = gql`
    mutation DeletePageCommentMutation($pageId: ID!, $commentId: ID!) {
        deletePageComment(pageId: $pageId, commentId: $commentId) {
            _id
            user {
                _id
                name
            }
            path
            style
            text
        }
    }
`;

const rotatePageMutation = gql`
    mutation RotatePageMutation($pageId: ID!, $rotation: Int) {
        rotatePage(pageId: $pageId, rotation: $rotation) {
            comments {
                _id
                user {
                    _id
                    name
                }
                path
                style
                text
            }
            rotation
        }
    }
`;

const marksArray = ['0', '-', '-.', '-+', '+/2', '+-', '+.', '+', '+!'];

class Paper extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        pages: PropTypes.array,
        editable: PropTypes.bool,
        showMarks: PropTypes.bool,
    };

    static defaultProps = {
        editable: false,
        showMarks: false,
    };

    state = {
        pageIndex: 0,
        inputPageIndex: 1,
        tool: null,
        showComments: true,
        marksOpen: false,
        pages: null,
        error: '',
        success: '',
    };

    componentDidMount() {
        const { pages } = this.props;

        if (pages[0] && pages[0]._id) {
            this.setState({
                pages: pages.map(page => ({
                    ...page,
                    comments: (page.comments || []).map(comment => ({
                        ...comment,
                        style: comment.style && JSON.parse(comment.style),
                    })),
                })),
            });
        } else {
            this.setState({ pages });
        }

        this.setMarks();

        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    setMarks(juryMarks = this.props.juryMarks) {
        const { round, level, tournament } = this.props;

        if (!tournament) return;

        const problems = tournament[round][level];
        let firstMarks = juryMarks.filter(({ stage }) => stage === 'first').reverse();
        firstMarks = problems.map(
            ({ name }) =>
                firstMarks.find(({ problemName }) => problemName === name) || {
                    mark: '',
                    user: null,
                    problemName: name,
                }
        );

        let secondMarks = juryMarks.filter(({ stage }) => stage === 'second').reverse();
        secondMarks = problems.map(
            ({ name }) =>
                secondMarks.find(({ problemName }) => problemName === name) || {
                    mark: '',
                    user: null,
                    problemName: name,
                }
        );

        let finalMarks = juryMarks.filter(({ stage }) => stage === 'final').reverse();
        finalMarks = problems.map(
            ({ name }) =>
                finalMarks.find(({ problemName }) => problemName === name) || {
                    mark: '',
                    user: null,
                    problemName: name,
                }
        );

        this.setState({
            firstMarks,
            secondMarks,
            finalMarks,
        });
    }

    handleNextClick = () => {
        if (this.state.pageIndex < this.props.pages.length - 1) {
            this.setState({
                pageIndex: this.state.pageIndex + 1,
                inputPageIndex: this.state.pageIndex + 2,
            });
        }
    };

    handlePreviousClick = () => {
        if (this.state.pageIndex > 0) {
            this.setState({
                pageIndex: this.state.pageIndex - 1,
                inputPageIndex: this.state.pageIndex,
            });
        }
    };

    handleToolClick = event => {
        const tool = event.currentTarget.dataset.tool;

        this.setState({
            tool: this.state.tool === tool ? null : tool,
        });
    };

    handleShowCommentsToggleClick = () => {
        this.setState({
            showComments: !this.state.showComments,
        });
    };

    handleMarksOpenToggleClick = () => {
        this.setState({
            marksOpen: !this.state.marksOpen,
        });
    };

    handleMarksChange = (value, stage, number, problemName) => {
        let marks = this.state[stage + 'Marks'].slice();
        marks[number] = { mark: value, user: null, problemName };

        this.setState({
            [stage + 'Marks']: marks,
        });
    };

    handleMarksSaveClick = () => {
        const newJuryMarks = [
            ...this.state.firstMarks
                .filter(m => !m.user && m.mark)
                .map(({ mark, problemName }) => ({ mark, problemName, stage: 'first' })),
            ...this.state.secondMarks
                .filter(m => !m.user && m.mark)
                .map(({ mark, problemName }) => ({ mark, problemName, stage: 'second' })),
            ...this.state.finalMarks
                .filter(m => !m.user && m.mark)
                .map(({ mark, problemName }) => ({ mark, problemName, stage: 'final' })),
        ];

        const juryMarksToDelete = [
            ...this.state.firstMarks
                .filter(m => !m.user && !m.mark)
                .map(({ mark, problemName }) => ({ mark, problemName, stage: 'first' })),
            ...this.state.secondMarks
                .filter(m => !m.user && !m.mark)
                .map(({ mark, problemName }) => ({ mark, problemName, stage: 'second' })),
            ...this.state.finalMarks
                .filter(m => !m.user && !m.mark)
                .map(({ mark, problemName }) => ({ mark, problemName, stage: 'final' })),
        ];

        this.props
            .updateJuryMarks({
                variables: { paperId: this.props._id, newJuryMarks, juryMarksToDelete },
            })
            .then(({ data: { updateJuryMarks: juryMarks } }) => {
                this.setMarks(juryMarks);
                this.setState({
                    success: 'Оценки сохранены',
                });
            })
            .catch(() => {
                this.setState({
                    error: 'Ошибка, не удалось сохранить оценки',
                });
            });
    };

    handleCreateComment = ({ path, style, text }) => {
        const { pages, pageIndex } = this.state;

        const page = pages[pageIndex];

        if (style) {
            style = JSON.stringify(style);
        }

        return this.props
            .createPageComment({
                variables: { pageId: page._id, path, style, text },
            })
            .then(({ data: { createPageComment: comments } }) => {
                page.comments = comments.map(comment => ({
                    ...comment,
                    style: comment.style && JSON.parse(comment.style),
                }));

                this.setState({
                    pages: pages.slice(),
                });
            });
    };

    handleDeleteComment = id => {
        const { pages, pageIndex } = this.state;

        const page = pages[pageIndex];

        return this.props
            .deletePageComment({
                variables: { pageId: page._id, commentId: id },
            })
            .then(({ data: { deletePageComment: comments } }) => {
                page.comments = comments.map(comment => ({
                    ...comment,
                    style: comment.style && JSON.parse(comment.style),
                }));

                this.setState({
                    pages: pages.slice(),
                });
            });
    };

    handleRotatePageClick = () => {
        const { pages, pageIndex } = this.state;

        const page = pages[pageIndex];

        return this.props
            .rotatePage({
                variables: { pageId: page._id, rotation: ((page.rotation || 0) + 270) % 360 },
            })
            .then(({ data: { rotatePage: { rotation, comments } } }) => {
                page.comments = comments.map(comment => ({
                    ...comment,
                    style: comment.style && JSON.parse(comment.style),
                }));
                page.rotation = rotation;

                this.setState({
                    pages: pages.slice(),
                });
            });
    };

    handleKeyDown = event => {
        if (event.target.tagName.toLowerCase() === 'input' || event.target.tagName.toLowerCase() === 'textarea') {
            return;
        }

        if (event.keyCode === 37) {
            this.handlePreviousClick();
        }

        if (event.keyCode === 39) {
            this.handleNextClick();
        }
    };

    handlePageInputChange = (name, value) => {
        value = value.replace(/\D/g, '');

        if (value !== '') {
            value = parseInt(value, 10);
        }

        this.setState({
            inputPageIndex: value,
        });
    };

    handlePageInputKeyDown = event => {
        if (!this.state.inputPageIndex) {
            return;
        }

        let pageIndex = this.state.inputPageIndex - 1;

        if (pageIndex < 0) {
            pageIndex = 0;
        }

        if (pageIndex > this.state.pages.length - 1) {
            pageIndex = this.state.pages.length - 1;
        }

        if (event.keyCode === 13) {
            this.setState({
                pageIndex,
                inputPageIndex: pageIndex + 1,
            });
        }
    };

    handleFinalMarksClick = () => {
        const { firstMarks, secondMarks, finalMarks } = this.state;

        if (
            firstMarks.every(firstMark => firstMark.mark) &&
            secondMarks.every(secondMark => secondMark.mark) &&
            finalMarks.every(finalMark => !finalMark.mark)
        ) {
            this.setState({
                finalMarks: secondMarks.map(({ mark, problemName }, i) => ({
                    mark: firstMarks[i].mark === mark ? mark : '',
                    user: null,
                    problemName,
                })),
            });
        }
    };

    renderToolbar() {
        const {
            round,
            level,
            tournament,
            intl: { formatMessage },
        } = this.props;
        const {
            tool,
            marksOpen,
            firstMarks,
            secondMarks,
            finalMarks,
            showComments,
            pages,
            inputPageIndex,
        } = this.state;

        const problems = tournament[round][level];

        return (
            <div className={classnames(styles.toolbar, { [styles.marksOpen]: marksOpen })}>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', background: tool === 'pencil' ? 'white' : 'none' }}
                    data-tool="pencil"
                    onClick={this.handleToolClick}
                    key="pencil"
                >
                    <Pencil color={tool === 'pencil' ? '#000000' : '#ffffff'} />
                </IconButton>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', background: tool === 'highlighter' ? 'white' : 'none' }}
                    data-tool="highlighter"
                    onClick={this.handleToolClick}
                    key="highlighter"
                >
                    <Highlighter color={tool === 'highlighter' ? '#000000' : '#ffffff'} />
                </IconButton>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', background: tool === 'text' ? 'white' : 'none' }}
                    data-tool="text"
                    onClick={this.handleToolClick}
                    key="text"
                >
                    <Text color={tool === 'text' ? '#000000' : '#ffffff'} />
                </IconButton>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', background: tool === 'eraser' ? 'white' : 'none' }}
                    data-tool="eraser"
                    onClick={this.handleToolClick}
                    key="eraser"
                >
                    <Eraser color={tool === 'eraser' ? '#000000' : '#ffffff'} />
                </IconButton>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', background: !showComments ? 'white' : 'none' }}
                    onClick={this.handleShowCommentsToggleClick}
                    key="visibility"
                >
                    <VisibilityOff color={!showComments ? '#000000' : '#ffffff'} />
                </IconButton>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px' }}
                    onClick={this.handleRotatePageClick}
                    key="rotate"
                >
                    <Rotate color="#ffffff" />
                </IconButton>
                {tournament.criteria &&
                    tournament.criteria[round][level] && (
                        <a href={tournament.criteria[round][level]} target="_blank">
                            <IconButton
                                iconStyle={{ width: '24px', height: '24px' }}
                                style={{ width: '48px', height: '48px' }}
                                key="rotate"
                            >
                                <Criteria color="#ffffff" />
                            </IconButton>
                        </a>
                    )}
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px' }}
                    onClick={this.handleMarksOpenToggleClick}
                    key="marksOpen"
                >
                    {marksOpen ? <ExpandLess color="#ffffff" /> : <ExpandMore color="#ffffff" />}
                </IconButton>
                {!marksOpen && (
                    <IconButton
                        iconStyle={{ width: '24px', height: '24px' }}
                        style={{ width: '48px', height: '48px', float: 'right' }}
                        onClick={this.props.onCloseClick}
                    >
                        <Close color="#ffffff" />
                    </IconButton>
                )}
                <div className={styles.pageNum}>
                    <TextField
                        name="pageNum"
                        value={inputPageIndex}
                        onChange={this.handlePageInputChange}
                        onKeyDown={this.handlePageInputKeyDown}
                        underlineStyle={{ color: 'white' }}
                        underlineFocusStyle={{ borderBottom: '1px solid white' }}
                        inputStyle={{
                            color: 'white',
                            fontSize: '16px',
                            height: '50px',
                            width: '28px',
                            textAlign: 'center',
                            top: '1px',
                        }}
                        style={{ width: '28px', height: '48px', marginTop: '-16px', marginRight: '8px' }}
                    />
                    {`/\u00A0\u00A0${pages.length}`}
                </div>
                {marksOpen && (
                    <div className={styles.marks}>
                        {problems.map((problem, number) => (
                            <AutoComplete
                                dataSource={marksArray}
                                filter={AutoComplete.noFilter}
                                hintText=""
                                searchText={firstMarks[number].mark}
                                textFieldStyle={{ width: '48px', height: '64px' }}
                                style={{ width: '48px', marginLeft: '16px', height: '64px', marginTop: '-8px' }}
                                underlineFocusStyle={{ color: 'white' }}
                                inputStyle={{ color: 'white', fontSize: '16px', height: '58px' }}
                                floatingLabelFixed
                                floatingLabelText={problem.name}
                                floatingLabelStyle={{ color: 'white' }}
                                floatingLabelFocusStyle={{ color: 'white' }}
                                title={firstMarks[number].user && firstMarks[number].user.name}
                                key={'first' + number}
                                onUpdateInput={value => this.handleMarksChange(value, 'first', number, problem.name)}
                                openOnFocus
                            />
                        ))}
                        <br />
                        {problems.map((problem, number) => (
                            <AutoComplete
                                dataSource={marksArray}
                                filter={AutoComplete.noFilter}
                                hintText=""
                                searchText={secondMarks[number].mark}
                                textFieldStyle={{ width: '48px', height: '64px' }}
                                style={{ width: '48px', marginLeft: '16px', marginTop: '-8px' }}
                                underlineFocusStyle={{ color: 'white' }}
                                inputStyle={{ color: 'white', fontSize: '16px', height: '58px' }}
                                floatingLabelFixed
                                floatingLabelText={problem.name}
                                floatingLabelStyle={{ color: 'white' }}
                                floatingLabelFocusStyle={{ color: 'white' }}
                                title={secondMarks[number].user && secondMarks[number].user.name}
                                key={'second' + number}
                                onUpdateInput={value => this.handleMarksChange(value, 'second', number, problem.name)}
                                openOnFocus
                            />
                        ))}
                        <br />
                        {problems.map((problem, number) => (
                            <AutoComplete
                                dataSource={marksArray}
                                filter={AutoComplete.noFilter}
                                hintText=""
                                searchText={finalMarks[number].mark}
                                textFieldStyle={{ width: '48px', height: '64px' }}
                                style={{ width: '48px', marginLeft: '16px', height: '64px', marginTop: '-8px' }}
                                underlineFocusStyle={{ color: 'white' }}
                                inputStyle={{ color: 'white', fontSize: '16px', height: '58px' }}
                                floatingLabelFixed
                                floatingLabelText={problem.name}
                                floatingLabelStyle={{ color: 'white' }}
                                floatingLabelFocusStyle={{ color: 'white' }}
                                title={finalMarks[number].user && finalMarks[number].user.name}
                                key={'final' + number}
                                onUpdateInput={value => this.handleMarksChange(value, 'final', number, problem.name)}
                                openOnFocus
                                onClick={this.handleFinalMarksClick}
                            />
                        ))}
                        <br />
                        <RaisedButton
                            className={styles.button}
                            label={formatMessage({ id: 'Paper.save' })}
                            onClick={this.handleMarksSaveClick}
                        />
                    </div>
                )}
            </div>
        );
    }

    renderBasicToolbar() {
        const { round, level, tournament } = this.props;
        const { finalMarks, showComments, pages, inputPageIndex } = this.state;

        const problems = tournament[round][level];

        return (
            <div className={styles.toolbar}>
                <div className={styles.basicToolbarMarks}>
                    {problems.map((problem, number) => (
                        <TextField
                            hintText=""
                            value={finalMarks[number].mark}
                            style={{
                                width: '32px',
                                marginLeft: '12px',
                                height: '64px',
                                marginTop: '-12px',
                                marginBottom: '-8px',
                                cursor: 'default',
                            }}
                            underlineFocusStyle={{ color: 'white' }}
                            inputStyle={{ color: 'white', fontSize: '16px', height: '56px' }}
                            floatingLabelFixed
                            floatingLabelText={problem.name}
                            floatingLabelStyle={{ color: 'white' }}
                            floatingLabelFocusStyle={{ color: 'white' }}
                            title={finalMarks[number].user && finalMarks[number].user.name}
                            key={'final' + number}
                            disabled
                        />
                    ))}
                </div>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', background: !showComments ? 'white' : 'none' }}
                    onClick={this.handleShowCommentsToggleClick}
                    key="visibility"
                >
                    <VisibilityOff color={!showComments ? '#000000' : '#ffffff'} />
                </IconButton>
                <IconButton
                    iconStyle={{ width: '24px', height: '24px' }}
                    style={{ width: '48px', height: '48px', float: 'right' }}
                    onClick={this.props.onCloseClick}
                >
                    <Close color="#ffffff" />
                </IconButton>
                <div className={styles.pageNum}>
                    <TextField
                        name="pageNum"
                        value={inputPageIndex}
                        onChange={this.handlePageInputChange}
                        onKeyDown={this.handlePageInputKeyDown}
                        underlineStyle={{ color: 'white' }}
                        underlineFocusStyle={{ borderBottom: '1px solid white' }}
                        inputStyle={{
                            color: 'white',
                            fontSize: '16px',
                            height: '50px',
                            width: '28px',
                            textAlign: 'center',
                            top: '1px',
                        }}
                        style={{ width: '28px', height: '48px', marginTop: '-16px', marginRight: '8px' }}
                    />
                    {`/\u00A0\u00A0${pages.length}`}
                </div>
            </div>
        );
    }

    render() {
        const { showMarks, editable } = this.props;
        const { pages, pageIndex, tool, showComments } = this.state;

        if (!pages) {
            return null;
        }

        const page = pages[pageIndex]._id ? pages[pageIndex] : { file: pages[pageIndex] };

        return (
            <BodyClassName className={styles.noScroll}>
                <div className={styles.paper}>
                    {pages[pageIndex] && (
                        <Page
                            {...page}
                            tool={tool}
                            onCreateComment={this.handleCreateComment}
                            onDeleteComment={this.handleDeleteComment}
                            showComments={showComments}
                        />
                    )}
                    {!showMarks && (
                        <div className={styles.closeIcon}>
                            <FloatingActionButton
                                backgroundColor="#000000"
                                disabledColor="#606060"
                                onClick={this.props.onCloseClick}
                                mini
                            >
                                <Close color="#ffffff" />
                            </FloatingActionButton>
                        </div>
                    )}
                    <div className={styles.previousIcon}>
                        <FloatingActionButton
                            backgroundColor="#000000"
                            disabledColor="#606060"
                            disabled={pageIndex === 0}
                            onClick={this.handlePreviousClick}
                        >
                            <img src={previousIcon} alt="Previous" />
                        </FloatingActionButton>
                    </div>
                    <div className={styles.nextIcon}>
                        <FloatingActionButton
                            backgroundColor="#000000"
                            disabledColor="#606060"
                            disabled={pageIndex === pages.length - 1}
                            onClick={this.handleNextClick}
                        >
                            <img src={nextIcon} alt="Next" />
                        </FloatingActionButton>
                    </div>
                    {showMarks && editable && this.renderToolbar()}
                    {showMarks && !editable && this.renderBasicToolbar()}
                    <Snackbar
                        open={!!this.state.error}
                        message={this.state.error}
                        autoHideDuration={10000}
                        onRequestClose={() => this.setState({ error: '' })}
                    />
                    <Snackbar
                        open={!!this.state.success}
                        message={this.state.success}
                        autoHideDuration={3000}
                        onRequestClose={() => this.setState({ success: '' })}
                    />
                </div>
            </BodyClassName>
        );
    }
}

export default compose(
    graphql(updateJuryMarksMutation, { name: 'updateJuryMarks' }),
    graphql(createPageCommentMutation, { name: 'createPageComment' }),
    graphql(deletePageCommentMutation, { name: 'deletePageComment' }),
    graphql(rotatePageMutation, { name: 'rotatePage' }),
    injectIntl
)(Paper);



// WEBPACK FOOTER //
// ./src/Components/Paper/Paper.js